import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, BarChart3, Flag } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import Footer from '../components/Footer';
import VerificationQueue from '../components/admin/VerificationQueue';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import FlagManagement from '../components/admin/FlagManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('verification');
  const [unverifiedPosts, setUnverifiedPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [flaggedContent, setFlaggedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({});
  const [resolving, setResolving] = useState({});
  const [deleting, setDeleting] = useState({});
  const [reporting, setReporting] = useState({});
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Admin access required',
        variant: 'destructive',
      });
      navigate('/myiniraa');
      return;
    }
    
    // Load data based on active tab
    if (activeTab === 'verification') {
      fetchUnverifiedPosts();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    } else if (activeTab === 'flags') {
      fetchFlaggedContent();
    }
  }, [isAdmin, navigate, activeTab]);

  const fetchFlaggedContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/flags?resolved=false`);
      setFlaggedContent(response.data);
    } catch (error) {
      toast({
        title: 'Failed to load flagged content',
        description: 'Please try refreshing the page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast({
        title: 'Failed to load analytics',
        description: 'Please try refreshing the page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnverifiedPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/posts/unverified`);
      setUnverifiedPosts(response.data);
    } catch (error) {
      toast({
        title: 'Failed to load posts',
        description: 'Please try refreshing the page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (postId) => {
    setVerifying({ ...verifying, [postId]: true });
    try {
      await axios.patch(`${API}/posts/${postId}/verify`);
      toast({
        title: 'Post verified!',
        description: 'The post has been marked as verified.',
      });
      // Remove from unverified list
      setUnverifiedPosts(unverifiedPosts.filter((post) => post.id !== postId));
    } catch (error) {
      toast({
        title: 'Failed to verify post',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setVerifying({ ...verifying, [postId]: false });
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    setDeleting({ ...deleting, [postId]: true });
    try {
      await axios.delete(`${API}/posts/${postId}`);
      toast({
        title: 'Post deleted',
        description: 'The post has been permanently removed.',
      });
      // Remove from unverified list
      setUnverifiedPosts(unverifiedPosts.filter((post) => post.id !== postId));
    } catch (error) {
      toast({
        title: 'Failed to delete post',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setDeleting({ ...deleting, [postId]: false });
    }
  };

  const handleReportPost = async (postId) => {
    setReporting({ ...reporting, [postId]: true });
    try {
      // Flag the post with admin reason
      await axios.post(`${API}/posts/${postId}/flag`, {
        reason: 'inappropriate',
        comment: 'Flagged by admin during verification review'
      });
      
      toast({
        title: 'Post reported',
        description: 'The post has been flagged for moderation review.',
      });
      
      // Optionally remove from unverified list or keep it
      // For now, we'll keep it in the list
    } catch (error) {
      toast({
        title: 'Failed to report post',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setReporting({ ...reporting, [postId]: false });
    }
  };

  const handleResolveFlag = async (flagId, action, contentType) => {
    setResolving({ ...resolving, [flagId]: true });
    try {
      const response = await axios.patch(`${API}/admin/flags/${flagId}/resolve`, { action });
      
      const actionMessages = {
        'approve': 'Flag dismissed - no action taken',
        'delete': `${contentType === 'post' ? 'Post' : 'Comment'} deleted successfully`,
        'warn': 'User warned - flag resolved',
        'ban': 'User banned - flag resolved'
      };
      
      toast({
        title: 'Flag resolved',
        description: actionMessages[action] || 'Action completed',
      });
      
      // Remove from flagged list
      setFlaggedContent(flaggedContent.filter((flag) => flag.id !== flagId));
    } catch (error) {
      
      
      toast({
        title: 'Failed to resolve flag',
        description: error.response?.data?.detail || error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setResolving({ ...resolving, [flagId]: false });
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F9F9] to-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#7B3F9E] mb-2" data-testid="admin-panel-title">
            Admin Panel
          </h1>
          <p className="text-gray-600">Manage and monitor your community</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('verification')}
              className={`pb-4 px-2 font-medium transition border-b-2 ${
                activeTab === 'verification'
                  ? 'text-[#FF8C00] border-[#FF8C00]'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>Verification Queue</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`pb-4 px-2 font-medium transition border-b-2 ${
                activeTab === 'analytics'
                  ? 'text-[#FF8C00] border-[#FF8C00]'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-5 h-5" />
                <span>Analytics</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('flags')}
              className={`pb-4 px-2 font-medium transition border-b-2 ${
                activeTab === 'flags'
                  ? 'text-[#FF8C00] border-[#FF8C00]'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Flag className="w-5 h-5" />
                <span>Flagged Content</span>
                {flaggedContent.length > 0 && (
                  <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                    {flaggedContent.length}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'verification' && (
          <VerificationQueue
            unverifiedPosts={unverifiedPosts}
            loading={loading}
            verifying={verifying}
            deleting={deleting}
            reporting={reporting}
            onVerify={handleVerify}
            onDelete={handleDeletePost}
            onReport={handleReportPost}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsDashboard
            analytics={analytics}
            loading={loading}
          />
        )}

        {activeTab === 'flags' && (
          <FlagManagement
            flaggedContent={flaggedContent}
            loading={loading}
            resolving={resolving}
            onResolveFlag={handleResolveFlag}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;
